<template>
  <Breadcrumbs :breadcrumbs="breadcrumbs" />

  <DynamicContent
    id="about-shop"
    class="container dynamic-content"
    :html="content?.introductionBlock"
  ></DynamicContent>

  <!-- This shop's folders -->
  <DynamicContent
    id="brochures-shop-content"
    class="container dynamic-content"
    :html="content?.similarBlock"
  ></DynamicContent>
  <BrochuresGrid
    :input="{ shopSlug: shopSlug }"
    :pagination="{ limit: 13, offset: 0 }"
    :impression-source="BrochureImpressionSource.Shop"
    :title="isContentBlockEmpty(content?.similarBlock) ? $t('shop.shop-brochures', [shopName]) : undefined"
    class="mb-2"
  />

  <!-- Comparable shop folders TODO for backend-->
  <DynamicContent
    id="similar-brochures-shop-content"
    class="container dynamic-content"
    :html="content?.timedBlock"
  ></DynamicContent>
  <SimilarBrochuresForShopGrid
    :input="{ shopSlug: shopSlug }"
    :tag="'h2'"
    :title="
      isContentBlockEmpty(content?.timedBlock) ? $t('shop.comparable-brochures', [shopSlug]) : undefined
    "
  />

  <!-- Ad -->
  <Advertisement :display-at="AdDisplayAt.Shop" class="ad mt-1 mb-1" />

  <!-- Offers -->
  <DynamicContent
    id="offers-shop-content"
    class="container dynamic-content"
    :html="content?.offersBlock"
  ></DynamicContent>
  <OffersGrid
    :pagination="{ limit: 8, offset: 0 }"
    :input="{ shopSlug: shopSlug }"
    :infinite-loading="false"
    :show-load-more="true"
    :tag="'h2'"
    :title="isContentBlockEmpty(content?.offersBlock) ? $t('shop.shop-offers', [shopName]) : undefined"
  />

  <CollapsibleSeoText
    :logo="data?.shop?.fileUrl || undefined"
    :title="shopName"
    :description="seoDescription"
    :tag="'h2'"
    class="mb-4"
  />

  <!-- Comparable shops TODO for backend -->
  <SimilarShopsForShopGrid
    :input="{
      shopSlug: shopSlug,
    }"
    :pagination="{ limit: 15, offset: 0 }"
    :title="$t('shop.comparable-shops')"
    :tag="'h2'"
    class="mb-4"
  />

  <!-- Comparable offers TODO for backend -->
  <!-- <OffersGrid
    :pagination="{ limit: 8, offset: 0, comparableOffersShopSlug: shopSlug }"
    :title="$t('shop.more-offers', [shopSlug])"
    /> -->

</template>

<script setup lang="ts">
  // This is the /shop/offers route, where we show the offers for a specific shop
  import { useWebsitePagePath } from "~/composables/useWebsitePath";
  import {
    AdDisplayAt,
    BrochureImpressionSource,
    useShopQuery,
    useWebsitePageQuery,
  } from "~/graphql/generated";

  // It is a bit unfortunate that it shares a slug with the catch-all route, but that's just the nuxt filesystem routing
  false && /*#__PURE__*/ defineI18nRoute({
    paths: {
      "nl-NL": "/[slug]/folder-aanbiedingen",
      fr: "/[slug]/folder-offres",
      nl: "/[slug]/folder-aanbiedingen",
    },
  });

  const shopSlug = useRoute().params.slug as string;

  // Fetch the shop by slug
  const { data, error } = await useShopQuery({
    variables: {
      shopInput: {
        id: shopSlug,
      },
    },
  });
  if (error?.value || !data.value?.shop) {
    throw createError({ statusCode: 404, fatal: true });
  }

  /// Fetch content for the this shop.
  const path = useRoute().path;
  const { websitePagePath } = useWebsitePagePath(path);
  const page = await useWebsitePageQuery({ variables: websitePagePath });
  const content = useDynamicContent(page.data);

  const shopName = computed(() => {
    return data.value?.shop?.name ?? "";
  });

  // The website page's meta info takes priority over the shop's own meta info, when a website page exists for this shop.
  const { metaTitle, metaDescription, seoDescription } = useContentPageMeta(
    computed(() => page.data.value?.websitePage),
    {
      metaTitle: computed(() => data.value?.shop?.metaTitle ?? undefined),
      metaDescription: computed(() => data.value?.shop?.metaDescription ?? undefined),
      description: computed(() => data.value?.shop?.description ?? undefined),
    },
  );

  // Set the breadcrumbs
  const breadcrumbs = [
    {
      to: "shops",
    },
    {
      to: "slug-shops",
      text: shopName.value,
    },
  ];

  // Set the page title
  const { t } = useI18n();
  useHead({
    title: computed(() => metaTitle.value || shopName.value),
    meta: [
      {
        name: "description",
        content: computed(
          () => metaDescription.value || t("shop.shop-brochures", [shopName.value]),
        ),
      },
    ],
  });

  useSeoMeta({
    ogTitle: computed(() => metaTitle.value || shopName.value),
    ogDescription: computed(
      () => metaDescription.value || t("shop.shop-brochures", [shopName.value]),
    ),
    ogImage: computed(() => data.value?.shop?.fileUrl || undefined),
    ogType: "article",
  });
</script>
